import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr, pollJob } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Collapse } from "../components/shared/Collapse";
import { BeforeAfter } from "../components/shared/BeforeAfter";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, AlertTriangle, Plus, X, ShieldCheck, Play, Circle, FlaskConical, Wrench, Sparkles } from "lucide-react";

const STATUS = {
  GENERATED: { l: "Generado", c: "text-slate-300 border-slate-500/30 bg-slate-500/10" },
  DRAFT: { l: "Generado", c: "text-slate-300 border-slate-500/30 bg-slate-500/10" },
  CONFIGURATION_REQUIRED: { l: "Requiere configuración", c: "text-amber-400 border-amber-500/30 bg-amber-500/10" },
  APPROVED: { l: "Aprobado", c: "text-cyan-400 border-cyan-500/30 bg-cyan-500/10" },
  ACTIVE: { l: "En implantación", c: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" },
  IN_TEST: { l: "En prueba", c: "text-orange-400 border-orange-500/30 bg-orange-500/10" },
  VERIFIED: { l: "Verificado", c: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" },
  NEEDS_ADJUSTMENT: { l: "Necesita ajustes", c: "text-orange-400 border-orange-500/30 bg-orange-500/10" },
  ARCHIVED: { l: "Archivado", c: "text-slate-500 border-slate-500/20 bg-slate-500/5" },
};

const isEmpty = (v) => v == null || (typeof v === "string" && (!v.trim() || v.toLowerCase().includes("unknown")));
const clean = (arr) => (arr || []).filter((x) => !isEmpty(typeof x === "string" ? x : JSON.stringify(x)));

function EditableList({ items, onChange, placeholder }) {
  const list = items || [];
  return (
    <div className="space-y-2">
      {list.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={it} onChange={(e) => { const n = [...list]; n[i] = e.target.value; onChange(n); }} className="bg-white/[0.03] border-white/10 text-sm" />
          <button onClick={() => onChange(list.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400 shrink-0"><X className="h-4 w-4" /></button>
        </div>
      ))}
      <button onClick={() => onChange([...list, ""])} className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300"><Plus className="h-3.5 w-3.5" /> {placeholder || "Añadir"}</button>
    </div>
  );
}

function List({ items, icon: Icon }) {
  const c = clean(items);
  if (!c.length) return <p className="text-slate-500 text-sm">—</p>;
  return <ul className="space-y-1.5 text-sm text-slate-300">{c.map((x, i) => <li key={i} className="flex gap-2">{Icon ? <Icon className="h-3.5 w-3.5 text-slate-500 shrink-0 mt-1" /> : <span className="text-amber-400">•</span>}<span>{x}</span></li>)}</ul>;
}

const guessUnit = (target) => {
  const t = (target || "").toLowerCase();
  if (/\d/.test(t) || /[<>≤≥%]/.test(t)) return t.includes("%") ? "percent" : "number";
  if (/\b(sí|si|no|booleano|verdadero|falso)\b/.test(t)) return "boolean";
  return "number";
};

export default function SystemDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sys, setSys] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [ev, setEv] = useState({ before: "", after: "", note: "", kpi: {} });
  const [result, setResult] = useState(null);
  const [adjust, setAdjust] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [redesign, setRedesign] = useState(null);
  const [redoing, setRedoing] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/systems/${id}`); setSys(data); } catch (e) { toast.error(apiErr(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const patch = async (fields) => {
    try { const { data } = await api.patch(`/systems/${id}`, fields); setSys(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setLocal = (k, v) => setSys((s) => ({ ...s, [k]: v }));

  const approve = async () => {
    try { await api.post(`/systems/${id}/approve`); toast.success("Sistema aprobado"); await load(); }
    catch (e) { const d = e?.response?.data?.detail; if (d?.missing) toast.error("Antes de aprobar, completa: " + d.missing.map((m) => m.label).join(", ")); else toast.error(apiErr(e)); }
  };
  const activate = async () => {
    try { await api.post(`/systems/${id}/activate`); toast.success("Sistema en marcha. Ahora impleméntalo paso a paso."); await load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleMilestone = async (key) => {
    try { const { data } = await api.post(`/systems/${id}/milestones/${key}`, { done: true }); setSys(data); toast.success("Hito completado"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openVerify = () => { setEv({ before: "", after: "", note: "", kpi: {} }); setVerifyOpen(true); };

  const startAdjust = () => { setShowFull(true); setAdjust(true); };
  const runRedesign = async () => {
    setRedoing(true);
    try {
      const { data } = await api.post(`/systems/${id}/redesign-async`);
      if (data.blocked) { toast.error(data.message || "Créditos insuficientes"); setRedoing(false); return; }
      const job = await pollJob(data.job_id, () => {});
      window.dispatchEvent(new Event("credits:refresh"));
      if (job.status === "DONE" && job.result) {
        setRedesign(job.result.proposal || {});
        toast.success(`Architect ha propuesto una corrección · −${job.result.credits_consumed} créditos`);
      } else {
        const { data: s } = await api.get(`/systems/${id}`);
        if (s.redesign_proposal?.proposal) { setSys(s); setRedesign(s.redesign_proposal.proposal); toast.success("Corrección lista para revisar"); }
        else toast.error("Architect no pudo completar el análisis. Tus créditos solo se cobran si termina.");
      }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setRedoing(false); }
  };
  const applyRedesign = async () => {
    try { const { data } = await api.patch(`/systems/${id}`, redesign); setSys(data); setRedesign(null); toast.success("Corrección aplicada. Vuelve a probar el sistema."); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doVerify = async () => {
    try {
      const kpi_results = (sys.kpi || []).filter((k) => !isEmpty(k?.name)).map((k, i) => ({
        name: k.name, target: k.target, value: ev.kpi[i] ?? "", unit_type: guessUnit(k.target),
      })).filter((r) => r.value !== "");
      const body = { kpi_results, note: ev.note };
      if (ev.before !== "" && ev.after !== "") { body.interventions_before = Number(ev.before); body.interventions_after = Number(ev.after); }
      const { data } = await api.post(`/systems/${id}/verify`, body);
      setVerifyOpen(false);
      window.dispatchEvent(new Event("credits:refresh"));
      await load();
      const met = (data.criteria || []).filter((c) => c.met === true).length;
      const total = (data.criteria || []).filter((c) => c.met !== null && c.met !== undefined).length;
      setResult({ ...data, met, total });
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!sys) return <div className="mono-label text-slate-500">CARGANDO…</div>;
  const st = STATUS[sys.status] || STATUS.GENERATED;
  const missing = sys.missing_requirements || [];
  const impl = sys.implementation || { milestones: [], total: 0, done: 0, next: null };
  const inImpl = ["ACTIVE", "IN_TEST", "NEEDS_ADJUSTMENT", "VERIFIED"].includes(sys.status);
  const lastV = sys.last_verification;
  const implPct = impl.total ? Math.round((impl.done / impl.total) * 100) : 0;

  return (
    <div className="space-y-5 max-w-3xl">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Volver</button>

      {/* HEADER — executive first */}
      <div className="card-arch p-5 sm:p-6">
        <div className="halo" />
        <div className="relative">
          <span className={`inline-block rounded-full border px-2.5 py-0.5 text-[0.65rem] font-mono ${st.c}`}>{st.l}</span>
          <h1 className="mt-2 font-head text-xl sm:text-2xl font-bold text-white leading-snug">{sys.title}</h1>
          {!isEmpty(sys.objective) && <p className="mt-2 text-sm text-slate-400">Objetivo: {sys.objective}</p>}

          {inImpl && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="mono-label text-cyan-400/80">Implementación</span>
                <span className="font-mono text-slate-400" data-testid="impl-progress-label">{impl.done} de {impl.total} hitos</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/5"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${implPct}%` }} /></div>
            </div>
          )}
        </div>
      </div>

      {/* NEXT STEP action zone */}
      <ActionZone
        sys={sys} missing={missing} impl={impl} lastV={lastV}
        onApprove={approve} onActivate={activate} onToggle={toggleMilestone}
        onVerify={openVerify} onAdjust={startAdjust} onRedesign={runRedesign} redoing={redoing} navigate={navigate}
      />

      {/* Missing config grouped prompt */}
      {missing.length > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="system-missing-banner">
          <p className="text-sm font-medium text-amber-300 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Faltan {missing.length} dato(s) para activar este sistema</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-300">{missing.map((m) => <li key={m.field}>• {m.label}</li>)}</ul>
        </div>
      )}

      {/* SECOND LEVEL — operational summary */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="card-arch p-4">
          <p className="mono-label text-cyan-400/80">Responsable</p>
          <Input data-testid="system-owner-input" value={isEmpty(sys.owner) ? "" : sys.owner} onChange={(e) => setLocal("owner", e.target.value)} onBlur={(e) => patch({ owner: e.target.value })} placeholder="¿Quién es responsable?" className="mt-2 bg-white/[0.03] border-white/10" />
        </div>
        <div className="card-arch p-4">
          <p className="mono-label text-cyan-400/80">Backup / sustituto</p>
          <Input data-testid="system-backup-input" value={isEmpty(sys.backup) ? "" : sys.backup} onChange={(e) => setLocal("backup", e.target.value)} onBlur={(e) => patch({ backup: e.target.value })} placeholder="¿Quién lo sustituye?" className="mt-2 bg-white/[0.03] border-white/10" />
        </div>
      </div>

      {clean(sys.limits).length > 0 && (
        <div className="card-arch p-4">
          <p className="mono-label text-cyan-400/80">Límites principales</p>
          <div className="mt-3"><List items={sys.limits.slice(0, 3)} /></div>
        </div>
      )}

      {/* THIRD LEVEL — progressive disclosure behind a single entry point */}
      <div>
        <Button data-testid="system-toggle-full" variant="outline" onClick={() => setShowFull((v) => !v)}
          className="w-full sm:w-auto bg-white/[0.03] border-white/10 text-white rounded-full">
          {showFull ? "Ocultar detalle del sistema" : "Ver sistema completo"}
        </Button>
      </div>
      {showFull && (
      <div className="space-y-3" data-testid="system-full-detail">
        {clean(sys.responsibilities).length > 0 && (
          <Collapse title="Responsabilidades" testId="sys-collapse-responsibilities"><List items={sys.responsibilities} /></Collapse>
        )}
        {(sys.decision_rights || []).length > 0 && (
          <Collapse title="Derechos de decisión" testId="sys-collapse-decision">
            <div className="space-y-3">
              {sys.decision_rights.map((dr, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-sm space-y-1.5">
                  {!isEmpty(dr.decide) && <p className="flex gap-2 text-emerald-300"><CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" /> Puede decidir: {dr.decide}</p>}
                  {!isEmpty(dr.limit) && <p className="text-slate-400 pl-6">Límite: {dr.limit}</p>}
                  {!isEmpty(dr.escalate) && <p className="flex gap-2 text-amber-300"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> Debe consultarte: {dr.escalate}</p>}
                  {!isEmpty(dr.exception) && <p className="text-slate-400 pl-6">Excepción: {dr.exception}</p>}
                </div>
              ))}
            </div>
          </Collapse>
        )}
        <Collapse title="Límites y decisiones (editar)" testId="sys-collapse-limits" open={adjust} onOpenChange={setAdjust}>
          <EditableList items={sys.limits} onChange={(v) => setLocal("limits", v)} placeholder="Añadir límite (ej. Compensación hasta 50 €)" />
          <Button variant="ghost" onClick={() => { patch({ limits: clean(sys.limits) }); toast.success("Límites guardados"); }} className="mt-2 text-xs text-slate-400 hover:text-white">Guardar límites</Button>
        </Collapse>
        {clean(sys.procedure).length > 0 && (
          <Collapse title="Procedimiento completo" testId="sys-collapse-procedure" defaultOpen={adjust}>
            <ol className="space-y-1.5 text-sm text-slate-300 list-decimal list-inside">{clean(sys.procedure).map((p, i) => <li key={i}>{p}</li>)}</ol>
          </Collapse>
        )}
        {(clean(sys.exceptions).length > 0 || clean(sys.escalation_rules).length > 0) && (
          <Collapse title="Excepciones y escalado" testId="sys-collapse-exceptions">
            {clean(sys.exceptions).length > 0 && <><p className="text-xs text-slate-500 mb-1">Excepciones</p><List items={sys.exceptions} /></>}
            {clean(sys.escalation_rules).length > 0 && <><p className="text-xs text-slate-500 mb-1 mt-3">Escalado</p><List items={sys.escalation_rules} /></>}
          </Collapse>
        )}
        {clean(sys.knowledge_transfer).length > 0 && (
          <Collapse title="Transferencia de conocimiento" testId="sys-collapse-knowledge"><List items={sys.knowledge_transfer} /></Collapse>
        )}
        {(sys.kpi || []).length > 0 && (
          <Collapse title="KPIs y criterios de verificación" testId="sys-collapse-kpi">
            <div className="grid sm:grid-cols-2 gap-2">
              {sys.kpi.map((k, i) => {
                const meas = (lastV?.criteria || []).find((c) => c.name === k.name);
                return (
                  <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-sm">
                    <b className="text-white">{isEmpty(k.name) ? "KPI" : k.name}</b>
                    <p className="text-slate-400">Objetivo: {isEmpty(k.target) ? "—" : k.target}</p>
                    {meas && <p className={`mt-1 text-xs ${meas.met ? "text-emerald-400" : meas.met === false ? "text-orange-400" : "text-slate-500"}`}>Último resultado: {String(meas.value)} {meas.met ? "· Cumplido" : meas.met === false ? "· No cumplido" : "· Sin evaluar"}</p>}
                  </div>
                );
              })}
            </div>
            {clean(sys.verification).length > 0 && <div className="mt-3"><p className="text-xs text-slate-500 mb-1">Verificación</p><List items={sys.verification} icon={Circle} /></div>}
          </Collapse>
        )}
      </div>
      )}

      <p className="text-xs text-slate-600">Contenido propuesto por Architect. Tú lo confirmas y editas: nada se da por cierto sin tu aprobación.</p>

      {/* REDESIGN PROPOSAL DIALOG */}
      <Dialog open={!!redesign} onOpenChange={(o) => !o && setRedesign(null)}>
        <DialogContent className="bg-[#0B0D13] border-white/10 text-slate-100 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-head text-white">Corrección propuesta por Architect</DialogTitle>
            <DialogDescription className="text-slate-400">Revisa los cambios. No se aplican hasta que confirmes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm" data-testid="redesign-proposal">
            {redesign?.limits?.length > 0 && <div><p className="mono-label text-slate-400">Límites</p><List items={redesign.limits} /></div>}
            {redesign?.exceptions?.length > 0 && <div><p className="mono-label text-slate-400">Excepciones</p><List items={redesign.exceptions} /></div>}
            {redesign?.procedure?.length > 0 && <div><p className="mono-label text-slate-400">Procedimiento</p><ol className="list-decimal list-inside text-slate-300 space-y-1">{redesign.procedure.map((p, i) => <li key={i}>{p}</li>)}</ol></div>}
            {!redesign?.limits?.length && !redesign?.exceptions?.length && !redesign?.procedure?.length && <p className="text-slate-500">Architect no propuso cambios estructurados. Ajusta manualmente los límites o el procedimiento.</p>}
          </div>
          <div className="mt-4 flex gap-2">
            <Button data-testid="redesign-apply" onClick={applyRedesign} className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 rounded-full font-semibold">Aplicar corrección</Button>
            <Button data-testid="redesign-discard" onClick={() => setRedesign(null)} variant="outline" className="bg-white/[0.03] border-white/10 text-white rounded-full">Descartar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* VERIFY DIALOG — evidence based */}
      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogContent className="bg-[#0B0D13] border-white/10 text-slate-100 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-head text-white">Registrar la prueba real</DialogTitle>
            <DialogDescription className="text-slate-400">Introduce solo datos del mundo real. INDEPENDENT evalúa si se cumplen los criterios.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-400">Intervenciones antes</label><Input data-testid="verify-before" inputMode="numeric" value={ev.before} onChange={(e) => setEv({ ...ev, before: e.target.value })} placeholder="ej. 8" className="mt-1 bg-white/[0.03] border-white/10" /></div>
              <div><label className="text-xs text-slate-400">Intervenciones ahora</label><Input data-testid="verify-after" inputMode="numeric" value={ev.after} onChange={(e) => setEv({ ...ev, after: e.target.value })} placeholder="ej. 2" className="mt-1 bg-white/[0.03] border-white/10" /></div>
            </div>
            {(sys.kpi || []).filter((k) => !isEmpty(k?.name)).map((k, i) => {
              const type = k.unit_type || guessUnit(k.target);
              return (
                <div key={i}>
                  <label className="text-xs text-slate-400">{k.name} <span className="text-slate-600">· Objetivo: {isEmpty(k.target) ? "—" : k.target}</span></label>
                  {type === "boolean" ? (
                    <div className="mt-1 flex gap-2">
                      {["Sí", "No"].map((opt) => (
                        <button key={opt} type="button" data-testid={`verify-kpi-${i}-${opt === "Sí" ? "yes" : "no"}`}
                          onClick={() => setEv({ ...ev, kpi: { ...ev.kpi, [i]: opt } })}
                          className={`flex-1 rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors ${ev.kpi[i] === opt ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300" : "border-white/10 bg-white/[0.03] text-slate-300"}`}>{opt}</button>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-1 flex items-center gap-2">
                      <Input data-testid={`verify-kpi-${i}`} inputMode="decimal" value={ev.kpi[i] ?? ""} onChange={(e) => setEv({ ...ev, kpi: { ...ev.kpi, [i]: e.target.value } })} placeholder="Resultado real" className="bg-white/[0.03] border-white/10" />
                      {type === "percent" && <span className="text-slate-400 text-sm">%</span>}
                    </div>
                  )}
                </div>
              );
            })}
            <div><label className="text-xs text-slate-400">Nota (opcional)</label><Input data-testid="verify-note" value={ev.note} onChange={(e) => setEv({ ...ev, note: e.target.value })} placeholder="¿Cómo fue la prueba?" className="mt-1 bg-white/[0.03] border-white/10" /></div>
          </div>
          <div className="mt-4">
            <Button data-testid="verify-submit" onClick={doVerify} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 rounded-full font-semibold">Evaluar prueba</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* RESULT / BEFORE-AFTER DIALOG */}
      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent className="bg-[#0B0D13] border-white/10 text-slate-100">
          <DialogHeader><DialogTitle className="sr-only">Resultado de la verificación</DialogTitle><DialogDescription className="sr-only">Resultado de la prueba y cambio en el Independence Score.</DialogDescription></DialogHeader>
          <div className="py-2" data-testid="verify-result-modal">
            {result?.total > 0 && <p className="text-center mono-label text-emerald-400 mb-3" data-testid="verify-criteria-summary">Prueba registrada · {result.met} de {result.total} criterios cumplidos</p>}
            {result?.result === "VERIFIED" ? (
              result?.score_delta ? <BeforeAfter delta={result.score_delta} /> : (
                <div className="text-center">
                  <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
                  <p className="mt-3 font-head text-lg font-bold text-white">Prueba verificada</p>
                  <p className="mt-1 text-sm text-slate-400">El sistema ya reduce esta dependencia.</p>
                </div>
              )
            ) : result?.result === "INSUFFICIENT_EVIDENCE" ? (
              <div className="text-center" data-testid="verify-insufficient">
                <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto" />
                <p className="mt-3 font-head text-lg font-bold text-white">Falta evidencia</p>
                <p className="mt-1 text-sm text-slate-400">{(result?.missing_evidence || []).join(", ") || "Registra al menos un dato real de la prueba."}</p>
              </div>
            ) : (
              <div className="text-center" data-testid="verify-needs-adjustment">
                <Wrench className="h-10 w-10 text-orange-400 mx-auto" />
                <p className="mt-3 font-head text-lg font-bold text-white">La prueba necesita ajustes</p>
                {(result?.failure_reasons || []).length > 0 && <p className="mt-1 text-sm text-slate-400">No cumplido: {result.failure_reasons.join(", ")}</p>}
                {result?.recommended_fix && <p className="mt-2 text-sm text-amber-300">{result.recommended_fix}</p>}
              </div>
            )}
            <Button data-testid="verify-result-continue" onClick={() => { const v = result?.result; setResult(null); if (v === "VERIFIED") navigate("/app"); }} className="mt-6 w-full bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold">{result?.result === "VERIFIED" ? "Continuar con el siguiente paso" : "Entendido"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ActionZone({ sys, missing, impl, lastV, onApprove, onActivate, onToggle, onVerify, onAdjust, onRedesign, redoing, navigate }) {
  const s = sys.status;

  if (s === "GENERATED" || s === "CONFIGURATION_REQUIRED") {
    return (
      <div className="card-arch p-5" data-testid="action-zone">
        <p className="mono-label text-amber-400">Siguiente paso</p>
        <p className="mt-2 text-sm text-slate-300">{missing.length ? "Completa la configuración obligatoria y aprueba el sistema." : "Revisa el sistema y apruébalo para empezar la implantación."}</p>
        <Button data-testid="system-approve-btn" onClick={onApprove} disabled={missing.length > 0} className="mt-4 w-full sm:w-auto bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold disabled:opacity-40"><ShieldCheck className="mr-2 h-4 w-4" /> Aprobar sistema</Button>
      </div>
    );
  }
  if (s === "APPROVED") {
    return (
      <div className="card-arch p-5" data-testid="action-zone">
        <p className="mono-label text-emerald-400">Siguiente paso</p>
        <p className="mt-2 text-sm text-slate-300">Pon el sistema en marcha para empezar la implantación real.</p>
        <Button data-testid="system-activate-btn" onClick={onActivate} className="mt-4 w-full sm:w-auto bg-emerald-500 text-black hover:bg-emerald-400 rounded-full font-semibold"><Play className="mr-2 h-4 w-4" /> Poner en marcha</Button>
      </div>
    );
  }
  if (s === "NEEDS_ADJUSTMENT") {
    return (
      <div className="card-arch p-5 border-orange-500/30" data-testid="action-zone-adjust">
        <p className="mono-label text-orange-400 flex items-center gap-2"><Wrench className="h-4 w-4" /> La prueba necesita ajustes</p>
        {lastV?.failure_reasons?.length > 0 && (
          <div className="mt-3"><p className="text-xs text-slate-500">Qué falló:</p>
            <ul className="mt-1 space-y-1 text-sm text-slate-300">{lastV.failure_reasons.map((r, i) => <li key={i} className="flex gap-2"><span className="text-orange-400">•</span>{r}</li>)}</ul>
          </div>
        )}
        {lastV?.recommended_fix && <p className="mt-3 text-sm text-amber-300">Siguiente paso recomendado: {lastV.recommended_fix}</p>}
        <div className="mt-4 flex flex-col sm:flex-row flex-wrap gap-2">
          <Button data-testid="system-adjust-btn" onClick={onAdjust} className="bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold"><Wrench className="mr-2 h-4 w-4" /> Ajustar sistema</Button>
          <Button data-testid="system-redesign-btn" onClick={onRedesign} disabled={redoing} variant="outline" className="bg-white/[0.03] border-white/10 text-white rounded-full"><Sparkles className="mr-2 h-4 w-4" /> {redoing ? "Analizando…" : "Analizar con Architect · 5 créditos"}</Button>
          <Button data-testid="system-retest-btn" onClick={onVerify} variant="outline" className="bg-white/[0.03] border-white/10 text-white rounded-full"><FlaskConical className="mr-2 h-4 w-4" /> Volver a probar</Button>
        </div>
      </div>
    );
  }
  if (s === "VERIFIED") {
    return (
      <div className="card-arch p-5 border-emerald-500/30" data-testid="action-zone">
        <p className="mono-label text-emerald-400 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Sistema verificado</p>
        <p className="mt-2 text-sm text-slate-300">Este sistema está funcionando y reduce la dependencia. Puedes registrar nuevas pruebas para consolidarlo.</p>
        <Button data-testid="system-reverify-btn" onClick={onVerify} variant="outline" className="mt-4 bg-white/[0.03] border-white/10 text-white rounded-full"><FlaskConical className="mr-2 h-4 w-4" /> Registrar otra prueba</Button>
      </div>
    );
  }
  // ACTIVE / IN_TEST — implementation milestones
  if (s !== "ACTIVE" && s !== "IN_TEST") {
    return (
      <div className="card-arch p-5" data-testid="action-zone">
        <p className="mono-label text-amber-400">Siguiente paso</p>
        <p className="mt-2 text-sm text-slate-300">Revisa el sistema y apruébalo para empezar la implantación.</p>
        <Button data-testid="system-approve-btn" onClick={onApprove} disabled={missing.length > 0} className="mt-4 w-full sm:w-auto bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold disabled:opacity-40"><ShieldCheck className="mr-2 h-4 w-4" /> Aprobar sistema</Button>
      </div>
    );
  }
  const next = impl.next;
  return (
    <div className="card-arch p-5" data-testid="action-zone">
      {next ? (
        <>
          <p className="mono-label text-emerald-400">Siguiente paso</p>
          <p className="mt-2 font-medium text-white">{next.title}</p>
          <p className="mt-1 text-sm text-slate-400">{next.description}</p>
          {next.key === "real_test" ? (
            <Button data-testid="system-verify-btn" onClick={onVerify} className="mt-4 w-full sm:w-auto bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold"><FlaskConical className="mr-2 h-4 w-4" /> Registrar prueba</Button>
          ) : (
            <Button data-testid="milestone-done-btn" onClick={() => onToggle(next.key)} className="mt-4 w-full sm:w-auto bg-emerald-500 text-black hover:bg-emerald-400 rounded-full font-semibold"><CheckCircle2 className="mr-2 h-4 w-4" /> Marcar como hecho</Button>
          )}
        </>
      ) : (
        <>
          <p className="mono-label text-emerald-400">Siguiente paso</p>
          <p className="mt-2 text-sm text-slate-300">Todos los hitos completados. Verifica el sistema con evidencia real.</p>
          <Button data-testid="system-verify-btn" onClick={onVerify} className="mt-4 w-full sm:w-auto bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold"><CheckCircle2 className="mr-2 h-4 w-4" /> Verificar sistema</Button>
        </>
      )}
      {/* Milestone checklist */}
      <div className="mt-5 border-t border-white/5 pt-4 space-y-2.5">
        {(impl.milestones || []).map((m) => (
          <div key={m.key} className="flex items-start gap-3 text-sm" data-testid={`milestone-${m.key}`}>
            {m.done ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" /> : <Circle className="h-4 w-4 text-slate-600 shrink-0 mt-0.5" />}
            <div className="min-w-0">
              <span className={m.done ? "text-slate-400 line-through" : "text-white"}>{m.title}</span>
              {m.auto && <span className="ml-2 text-[0.6rem] font-mono text-cyan-500/70">AUTO</span>}
            </div>
            {!m.done && !m.auto && m.key !== next?.key && (
              <button onClick={() => onToggle(m.key)} className="ml-auto text-xs text-amber-400 hover:text-amber-300 shrink-0">Hecho</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
